// ITER221 — Super Admin: Apify Provider Console
//
// Cross-tenant visibility + provider kill-switch:
//   • Provider lineup + token status
//   • Per-tenant monthly spend, calls, cache-hit %
//   • Global enable/disable toggle (gates ALL tenants instantly)
//
// Stub-mode-aware. Loads without any Apify token.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import {
  Zap, Activity, CheckCircle2, AlertTriangle, Sparkles,
  ShieldCheck, Building2, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pct   = (n) => `${(Number(n) || 0).toFixed(0)}%`;

const StatTile = ({ label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    slate:  'border-slate-200 bg-white',
    green:  'border-emerald-200 bg-emerald-50/50',
    amber:  'border-amber-200 bg-amber-50/50',
    rose:   'border-rose-200 bg-rose-50/50',
  };
  return (
    <div data-testid={testid}
          className={`rounded-md border px-4 py-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

export default function SuperAdminApify() {
  useAuth();   // ensures auth context bootstrap is mounted
  const headersFn = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [config, setConfig]   = useState(null);
  const [usage, setUsage]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = headersFn();
      const [c, u] = await Promise.all([
        axios.get(`${API}/api/expansion-intelligence/super-admin/apify/config`, { headers }),
        axios.get(`${API}/api/expansion-intelligence/super-admin/apify/tenants`, { headers }),
      ]);
      setConfig(c.data?.data || null);
      setUsage(u.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Apify config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (next) => {
    setToggling(true);
    try {
      await axios.post(
        `${API}/api/expansion-intelligence/super-admin/apify/config`,
        { enabled: next },
        { headers: headersFn() },
      );
      toast.success(next ? 'Apify enabled globally' : 'Apify disabled globally');
      await load();
    } catch (e) {
      toast.error('Toggle failed');
    } finally {
      setToggling(false);
    }
  };

  const totalSpend = (usage?.tenants || [])
    .reduce((acc, t) => acc + Number(t.total_cost_usd || 0), 0);
  const totalCalls = (usage?.tenants || [])
    .reduce((acc, t) => acc + Number(t.total_calls || 0), 0);
  const totalHits = (usage?.tenants || [])
    .reduce((acc, t) => acc + Number(t.cache_hits || 0), 0);
  const overallHitPct = totalCalls > 0
    ? (totalHits / totalCalls) * 100 : 0;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="super-admin-apify">
      {/* ─── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-slate-700" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Apify Provider Console
            </h1>
            <p className="text-xs text-slate-500">
              Cross-tenant enrichment governance · provider kill-switch · spend
              monitoring. Read-only when running in stub mode.
            </p>
          </div>
        </div>
        <Button
          data-testid="apify-refresh-btn"
          onClick={load}
          variant="outline"
          disabled={loading}
          className="text-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ─── Config + Toggle ────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-slate-600" />
              <h2 className="text-sm font-semibold text-slate-900">Provider Status</h2>
              {config?.is_live ? (
                <span data-testid="apify-global-mode"
                       className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium">
                  <CheckCircle2 className="h-3 w-3" /> LIVE
                </span>
              ) : (
                <span data-testid="apify-global-mode"
                       className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-xs font-medium">
                  <Sparkles className="h-3 w-3" /> Stub
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Globally enabled</span>
              <Switch
                data-testid="apify-global-toggle"
                checked={!!config?.enabled}
                disabled={toggling}
                onCheckedChange={handleToggle}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              testid="apify-tile-token"
              label="Token configured"
              value={config?.token_configured ? 'Yes' : 'No'}
              tone={config?.token_configured ? 'green' : 'amber'}
              sub={config?.token_configured
                ? 'Reads APIFY_API_TOKEN from env'
                : 'Add APIFY_API_TOKEN to backend/.env to go live'}
            />
            <StatTile
              testid="apify-tile-cap"
              label="Default soft cap"
              value={money(config?.soft_cap_usd)}
              sub="Per tenant, per month"
            />
            <StatTile
              testid="apify-tile-spend"
              label="Total spend (mo)"
              value={money(totalSpend)}
              sub={`Across ${(usage?.tenants || []).length} tenant${(usage?.tenants || []).length === 1 ? '' : 's'}`}
            />
            <StatTile
              testid="apify-tile-hit"
              label="Cache hit % (mo)"
              value={pct(overallHitPct)}
              sub={`${totalHits} hits / ${totalCalls} calls`}
            />
          </div>

          {/* Provider lineup */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-slate-200 pt-3">
            {Object.entries(config?.providers || {}).map(([platform, info]) => (
              <div key={platform}
                    data-testid={`apify-provider-${platform}`}
                    className="rounded-md border border-slate-200 px-3 py-2 bg-slate-50/50">
                <div className="flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-800 capitalize">
                    {platform}
                  </span>
                </div>
                <div className="text-[11px] text-slate-600 mt-1">
                  Primary: <code className="bg-white px-1 rounded">{info.primary}</code>
                </div>
                <div className="text-[11px] text-slate-600">
                  Fallback: <code className="bg-white px-1 rounded">{info.fallback}</code>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ─── Per-tenant table ───────────────────────────────────────── */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-900">Tenant Spend (this month)</h2>
          </div>
          {(usage?.tenants || []).length === 0 ? (
            <div className="text-xs text-slate-500 py-6 text-center"
                  data-testid="apify-no-tenants">
              No enrichment activity recorded yet this month. As soon as a
              tenant enriches a project, they will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="apify-tenant-table">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-3">Tenant</th>
                    <th className="py-1.5 pr-3">Calls</th>
                    <th className="py-1.5 pr-3">Cache Hits</th>
                    <th className="py-1.5 pr-3">Cache %</th>
                    <th className="py-1.5 pr-3">Zomato</th>
                    <th className="py-1.5 pr-3">Swiggy</th>
                    <th className="py-1.5 pr-3 text-right">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {(usage?.tenants || []).map((t) => (
                    <tr key={t.tenant_id} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{t.tenant_id}</td>
                      <td className="py-1.5 pr-3">{t.total_calls || 0}</td>
                      <td className="py-1.5 pr-3">{t.cache_hits || 0}</td>
                      <td className="py-1.5 pr-3">{pct(t.cache_hit_pct)}</td>
                      <td className="py-1.5 pr-3">{t.calls?.zomato || 0}</td>
                      <td className="py-1.5 pr-3">{t.calls?.swiggy || 0}</td>
                      <td className="py-1.5 pr-3 text-right">{money(t.total_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {!config?.token_configured && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-4 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
            <div className="text-xs text-amber-900">
              <div className="font-semibold mb-0.5">Apify is running in stub mode.</div>
              The entire enrichment pipeline (cache, derived intelligence, UI,
              cost tracking) is fully functional with deterministic fixtures.
              Add <code className="bg-white rounded px-1">APIFY_API_TOKEN</code> to
              <code className="bg-white rounded px-1 ml-1">backend/.env</code> and
              restart the backend — the system flips to live calls without any
              code change. No Webshare proxy is configured at this stage.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
